import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from 'lib/utils'
import { AdasBox } from './AdasBox'
import { AdasListItemIcon } from './AdasListItemIcon'
import { AdasListItemText } from './AdasListItemText'
import { Collapsible, CollapsibleContent } from '@components/ui/collapsible'

export const AdasMenuList = React.forwardRef(
  ({ items = [], onClickAway, variant, open, pathName, onItemClick, isMobile = false, sx, ...rest }, ref) => {
    const [openItems, setOpenItems] = useState({})
    const containerRef = useRef(null)

    const handleClickAway = () => {
      if (onClickAway && variant === 'withClickAway') onClickAway()
    }

    useEffect(() => {
      if (variant !== 'withClickAway') return
      const handleMouseDown = (e) => {
        if (containerRef.current && !containerRef.current.contains(e.target)) {
          handleClickAway()
        }
      }
      document.addEventListener('mousedown', handleMouseDown)
      return () => document.removeEventListener('mousedown', handleMouseDown)
    }, [variant, onClickAway])

    const isActive = (item) =>
      (pathName === '/' && item.childPathPrefix === '/') || (item.childPathPrefix !== '/' && pathName?.includes(item.childPathPrefix))

    const handleItemClick = (item) => {
      if (item.children) {
        setOpenItems((prev) => ({ ...prev, [item.label]: !prev[item.label] }))
      } else if (item.path) {
        onItemClick(item.path)
      } else if (item.onClick) {
        item.onClick()
      }
    }

    const renderItem = (item, index) => (
      <AdasBox key={index} className={isMobile ? '' : 'mb-0.5'}>
        {item.children && <hr className='my-2 opacity-30 border-divider' />}
        <button
          onClick={() => (variant === 'withClickAway' ? item.onClick?.() : handleItemClick(item))}
          className={cn(
            'flex items-center w-full px-3 py-2.5 text-[14px] font-medium transition-colors',
            isMobile ? 'hover:bg-muted rounded-lg' : 'hover:bg-white/10',
            isActive(item) && !item.children && !isMobile && 'bg-sidebar-active-bg border-l-[3px] border-sidebar-active-border rounded-md',
            isActive(item) && !item.children && isMobile && 'bg-primary/10 rounded-lg'
          )}
        >
          <AdasListItemIcon className='min-w-0 mr-3'>
            {item.icon && (
              <item.icon
                className={cn(
                  'w-[18px] h-[18px]',
                  isMobile
                    ? isActive(item) && !item.children
                      ? 'text-primary'
                      : 'text-muted-foreground'
                    : isActive(item) && !item.children
                      ? 'text-secondary'
                      : 'text-custom-sidebarText'
                )}
              />
            )}
          </AdasListItemIcon>
          {open && (
            <AdasListItemText className='flex-1 text-left overflow-hidden'>
              <span
                className={cn(
                  'text-[14px] font-medium truncate',
                  isMobile
                    ? isActive(item) && !item.children
                      ? 'text-primary'
                      : 'text-foreground'
                    : isActive(item) && !item.children
                      ? 'text-white'
                      : 'text-custom-sidebarText'
                )}
              >
                {item.label}
              </span>
            </AdasListItemText>
          )}
          {item.children &&
            open &&
            (openItems[item.label] ? (
              <ChevronDown className={cn('w-4 h-4', isMobile ? 'text-muted-foreground' : 'text-custom-sidebarText')} />
            ) : (
              <ChevronRight className={cn('w-4 h-4', isMobile ? 'text-muted-foreground' : 'text-custom-sidebarText')} />
            ))}
        </button>
        {item.children && (
          <Collapsible open={!!openItems[item.label]}>
            <CollapsibleContent>
              <AdasBox className={cn('transition-all', open ? 'pl-4' : 'pl-0')}>
                <AdasMenuList
                  variant='withoutClickAway'
                  onItemClick={onItemClick}
                  pathName={pathName}
                  open={open}
                  items={item.children}
                  isMobile={isMobile}
                />
              </AdasBox>
            </CollapsibleContent>
          </Collapsible>
        )}
      </AdasBox>
    )

    const list = (
      <div ref={ref} {...rest}>
        {items.map(renderItem)}
      </div>
    )

    if (variant === 'withClickAway') {
      return <div ref={containerRef}>{list}</div>
    }
    return list
  }
)

AdasMenuList.displayName = 'AdasMenuList'

AdasMenuList.displayName = 'AdasMenuList'

AdasMenuList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      icon: PropTypes.elementType,
      path: PropTypes.string,
      onClick: PropTypes.func,
      children: PropTypes.array,
      childPathPrefix: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  onClickAway: PropTypes.func,
  variant: PropTypes.oneOf(['withClickAway', 'withoutClickAway']),
  open: PropTypes.bool,
  pathName: PropTypes.string,
  onItemClick: PropTypes.func
}
